/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { createContext, type ReactNode, useContext, useMemo } from 'react';

export type ProposalSurface = 'actions_center' | 'sidebar';

/**
 * Controls surface-aware rendering in components that embed research briefs.
 *
 * - 'sidebar': Research brief is wrapped in a CollapsibleSection (defaultExpanded=true)
 *   to allow the user to collapse it in the crowded sidebar panel.
 * - 'actions_center': Research brief renders inline without wrapping, since the
 *   actions centre already provides its own layout chrome.
 *
 * Components that do not render inside a ProposalSurfaceProvider default to 'actions_center'.
 */
const ProposalSurfaceContext = createContext<{ surface: ProposalSurface }>({
  surface: 'actions_center',
});

export function ProposalSurfaceProvider({
  surface,
  children,
}: {
  surface: ProposalSurface;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ surface }), [surface]);
  return (
    <ProposalSurfaceContext.Provider value={value}>
      {children}
    </ProposalSurfaceContext.Provider>
  );
}

export function useProposalSurface(): { surface: ProposalSurface } {
  return useContext(ProposalSurfaceContext);
}
